import * as THREE from 'three';
import { cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * The heart (docs/sendas/artifice.md, chapter 3): its people and La Tenaza, painted miniatures.
 *
 * - La Tenaza: a slag crab the size of a cart. A wide low carapace of cooled lava, black-brown crust in plates with
 *   seams of fire between them and embers in every joint; two great pincers in front (the right a little bigger), each
 *   a fixed jaw and a jaw that opens on its hinge, their edges notched like a quarry's wedge; six legs pointed like
 *   picks; eyes on short stalks, red. Its bones: the body, the arms, the claws, the jaws, the legs and knees, so it
 *   walks, opens its pincers wide (its warning), snaps, grips (anim/creatures/tenaza.ts).
 * - Adela of Grada: seventy, short and broad, grey hair in a knot, a quarry's dust apron over slate wool, hands too
 *   big for her, the pick on her shoulder.
 * - Quarrymen of Grada: leather caps and stone-dust aprons, a maul or a pick, sleeves rolled.
 * - Brask: a dwarf of sixty, broad, a dark travelling cloak with its hood down, a grey-black beard in three braids with
 *   iron rings, a hammer at his belt, the clan's mark (a hammer and a key) in red on his shoulder clasp.
 * - The clan: dwarves hooded in slate and rust, beards, hammers and picks, the clan's mark on a strap.
 *
 * Colors: cold slate, iron and wool; the crab's crust near black with the dragon's red-orange in its seams (never gold).
 */

const CRUST = '#2a2523';
const CRUST_L = '#3d3631';
const SEAM = '#ff6a2a';
const RED = '#ff4f3a';

/* ---------- La Tenaza ---------- */

export const tenaza = (o?: BaseOpts): Model => {
  const c = begin(o, 0.95, 913);
  const g = c.g;
  const crust = stone(CRUST, { roughness: 0.92 });
  const crustL = stone(CRUST_L, { roughness: 0.9 });
  const hot = emis('#5a1408', SEAM, 2.2);
  const ember = emis('#ffb07a', SEAM, 2.6);
  const jawM = std('#1d1a19', { roughness: 0.55, metalness: 0.25, flatShading: true });
  const edge = std('#6b625a', { roughness: 0.4, metalness: 0.35, flatShading: true });
  const body = joint(g, V(0, 0.5, 0), 'body');
  const B = body.abs;
  // The carapace: wide and low, its rim ragged; plates of crust on top with fire in the seams between.
  const shell = M(new THREE.SphereGeometry(0.62, 12, 8), crust, B);
  shell.position.set(0, 0.52, -0.02);
  shell.scale.set(1.38, 0.44, 1.05);
  const belly = M(new THREE.SphereGeometry(0.5, 10, 6), crustL, B);
  belly.position.set(0, 0.4, 0);
  belly.scale.set(1.2, 0.34, 0.95);
  const plates: [number, number, number, number][] = [
    [0, 0.76, -0.05, 0.24],
    [-0.34, 0.7, 0.1, 0.19],
    [0.36, 0.7, 0.08, 0.2],
    [-0.28, 0.68, -0.3, 0.17],
    [0.3, 0.68, -0.32, 0.18],
    [0.02, 0.72, 0.28, 0.17],
    [-0.58, 0.58, -0.05, 0.14],
    [0.6, 0.58, -0.08, 0.15],
  ];
  for (const [x, y, z, r] of plates) {
    const p = M(new THREE.DodecahedronGeometry(r, 0), crustL, B);
    p.position.set(x, y, z);
    p.scale.set(1.3, 0.5, 1.1);
    p.rotation.y = x * 3 + z;
  }
  // The seams: thin bars of fire between the plates, and a glow under the rim.
  const seams: [number, number, number, number, number][] = [
    [-0.17, 0.79, 0.05, 0.36, 0.6],
    [0.18, 0.79, 0.02, 0.34, -0.5],
    [0, 0.78, -0.2, 0.44, 1.5],
    [-0.45, 0.68, -0.15, 0.3, 1.1],
    [0.46, 0.68, -0.18, 0.3, -1.1],
    [0.0, 0.76, 0.16, 0.3, 1.6],
  ];
  for (const [x, y, z, l, a] of seams) {
    const s = M(new THREE.BoxGeometry(l, 0.022, 0.03), hot, B);
    s.position.set(x, y, z);
    s.rotation.y = a;
  }
  glowSprite(B, SEAM, 1.3, V(0, 0.82, -0.02), 0.32);
  // The mouth: a dark slot under the front rim, embers in it.
  const mouth = M(new THREE.BoxGeometry(0.34, 0.06, 0.05), ember, B);
  mouth.position.set(0, 0.44, 0.62);
  // Eyes on short stalks, red.
  const eyes = joint(B, V(0, 0.66, 0.5), 'eyes');
  for (const k of [-1, 1]) {
    limb(eyes.abs, V(k * 0.1, 0.62, 0.5), V(k * 0.14, 0.8, 0.6), 0.025, 0.02, crust, 5);
    M(new THREE.SphereGeometry(0.045, 8, 6), emis('#ffd0c4', RED, 2.6), eyes.abs).position.set(k * 0.14, 0.82, 0.61);
  }
  glowSprite(eyes.abs, RED, 0.35, V(0, 0.82, 0.64), 0.7);
  const skel: Record<string, THREE.Object3D> = { body: body.p, eyes: eyes.p };
  // The pincers: an arm out front at each corner, the claw at its end, its moving jaw on a hinge.
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const big = k > 0 ? 1.12 : 1;
    const arm = joint(B, V(k * 0.5, 0.5, 0.42), `arm${s}`);
    limb(arm.abs, V(k * 0.5, 0.5, 0.42), V(k * 0.78, 0.56, 0.78), 0.09, 0.08, crust, 7);
    M(new THREE.SphereGeometry(0.1, 8, 6), ember, arm.abs).position.set(k * 0.5, 0.5, 0.42);
    const claw = joint(arm.abs, V(k * 0.78, 0.56, 0.78), `claw${s}`);
    M(new THREE.SphereGeometry(0.09, 8, 6), ember, claw.abs).position.set(k * 0.78, 0.56, 0.78);
    // The palm of the claw and its fixed jaw (the lower), notched.
    const palm = M(new THREE.SphereGeometry(0.2 * big, 10, 7), crust, claw.abs);
    palm.position.set(k * 0.8, 0.56, 0.98);
    palm.scale.set(0.95, 0.8, 1.25);
    const lower = M(new THREE.ConeGeometry(0.1 * big, 0.5 * big, 6), jawM, claw.abs);
    lower.position.set(k * 0.8, 0.5, 1.32);
    lower.rotation.x = Math.PI / 2;
    lower.scale.set(1.1, 1, 0.6);
    for (let n = 0; n < 3; n++) M(new THREE.BoxGeometry(0.05, 0.05, 0.05), edge, claw.abs).position.set(k * 0.8, 0.56, 1.18 + n * 0.1);
    // The moving jaw (the upper), on its hinge at the palm's top.
    const jaw = joint(claw.abs, V(k * 0.8, 0.66, 1.02), `jaw${s}`);
    const upper = M(new THREE.ConeGeometry(0.09 * big, 0.52 * big, 6), jawM, jaw.abs);
    upper.position.set(k * 0.8, 0.66, 1.34);
    upper.rotation.x = Math.PI / 2;
    upper.scale.set(1.1, 1, 0.55);
    for (let n = 0; n < 3; n++) M(new THREE.BoxGeometry(0.05, 0.05, 0.05), edge, jaw.abs).position.set(k * 0.8, 0.6, 1.2 + n * 0.1);
    // Fire in the hinge.
    M(new THREE.SphereGeometry(0.05, 6, 5), ember, jaw.abs).position.set(k * 0.8, 0.66, 1.02);
    Object.assign(skel, { [`arm${s}`]: arm.p, [`claw${s}`]: claw.p, [`jaw${s}`]: jaw.p });
  }
  // Six legs like picks, three a side, front to back (0–2 the right, 3–5 the left).
  for (let i = 0; i < 6; i++) {
    const k = i < 3 ? 1 : -1;
    const z = 0.28 - (i % 3) * 0.3;
    const hip = V(k * 0.62, 0.42, z);
    const kneeAt = V(k * 1.02, 0.62, z + 0.05);
    const foot = V(k * 1.24, 0.0, z + 0.1);
    const leg = joint(B, hip, `leg${i}`);
    limb(leg.abs, hip, kneeAt, 0.06, 0.05, crust, 6);
    M(new THREE.SphereGeometry(0.05, 6, 5), ember, leg.abs).position.copy(hip);
    const knee = joint(leg.abs, kneeAt, `knee${i}`);
    M(new THREE.SphereGeometry(0.05, 6, 5), ember, knee.abs).position.copy(kneeAt);
    limb(knee.abs, kneeAt, foot, 0.05, 0.015, jawM, 6);
    Object.assign(skel, { [`leg${i}`]: leg.p, [`knee${i}`]: knee.p });
  }
  return done(c, 1.2, undefined, undefined, bonesOf(c, skel));
};

/* ---------- the people ---------- */

export interface Folk {
  coat: string;
  under: string;
  legs: string;
  skin: string;
  hair: string;
  /** Short and broad (a dwarf). */
  dwarf?: boolean;
  beard?: string;
  /** What is on the shoulder or in the hand. */
  tool?: 'pick' | 'maul' | 'hammer';
  apron?: string;
  cap?: string;
  hood?: string;
  cloak?: string;
  bun?: boolean;
  mark?: boolean;
}

export const folk = (o: BaseOpts | undefined, seed: number, f: Folk): Model => {
  const c = begin(o, 0.6, seed);
  const skin = std(f.skin, { roughness: 0.88 });
  const coat = cloth(f.coat);
  const fig = figure({ legs: cloth(f.legs), boots: leather('#221c18'), torso: coat, shoulders: coat, arms: cloth(f.under), forearms: cloth(f.under), hands: skin, skin });
  c.g.add(fig.fig);
  if (f.dwarf) fig.fig.scale.set(1.22, 0.8, 1.18);
  const b = fig.bodyAbs;
  const h = fig.headAbs;
  const hair = std(f.hair, { roughness: 1 });
  if (f.apron) {
    const ap = M(new THREE.BoxGeometry(0.34, 0.6, 0.02), leather(f.apron), b);
    ap.position.set(0, 1.04, 0.2);
    ap.rotation.x = -0.06;
  }
  if (f.cloak) {
    // A travelling cloak from the shoulders to the shins, open in front; its hood down on the back.
    const cl = M(new THREE.CylinderGeometry(0.2, 0.34, 1.05, 12, 1, true, Math.PI * 0.62, Math.PI * 1.76), cloth(f.cloak), b);
    cl.position.set(0, 0.98, -0.02);
    const hd = M(new THREE.SphereGeometry(0.13, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.5), cloth(f.cloak), b);
    hd.position.set(0, 1.44, -0.16);
    hd.rotation.x = -1.2;
  }
  if (f.hood) {
    const hd = M(new THREE.SphereGeometry(0.15, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.62), cloth(f.hood), h);
    hd.position.set(0, 1.64, -0.02);
    hd.scale.set(1.06, 1.1, 1.12);
  } else if (f.cap) {
    const cap = M(new THREE.SphereGeometry(0.138, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.42), leather(f.cap), h);
    cap.position.set(0, 1.66, -0.01);
    M(new THREE.BoxGeometry(0.2, 0.012, 0.08), leather(f.cap), h).position.set(0, 1.7, 0.12);
  } else {
    const top = M(new THREE.SphereGeometry(0.134, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
    top.position.set(0, 1.652, -0.012);
    if (f.bun) M(new THREE.SphereGeometry(0.06, 8, 6), hair, h).position.set(0, 1.72, -0.11);
  }
  if (f.beard) {
    const bm = std(f.beard, { roughness: 1 });
    const beard = M(new THREE.SphereGeometry(0.12, 10, 8, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), bm, h);
    beard.position.set(0, 1.56, 0.05);
    beard.scale.set(1.15, 1.3, 0.9);
    // Three braids with iron rings.
    for (const k of [-1, 0, 1]) {
      for (let i = 0; i < 3; i++) M(new THREE.SphereGeometry(0.026 - i * 0.003, 6, 5), bm, h).position.set(k * 0.045, 1.43 - i * 0.05, 0.12 - i * 0.006);
      ring(h, 0.03, 0.007, metal('#5a6069'), 1.44, 3, 10).position.set(k * 0.045, 1.4, 0.115);
    }
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.046, 0.012, 0.016), hair, h).position.set(k * 0.044, 1.686, 0.11);
  }
  const nose = M(new THREE.SphereGeometry(0.03, 8, 6), std(f.skin, { roughness: 0.8 }), h);
  nose.position.set(0, 1.62, 0.13);
  nose.scale.set(1, 1.1, 1.2);
  if (f.mark) {
    // The clan's mark on a strap across the chest: a hammer and a key, red.
    const strap = M(new THREE.BoxGeometry(0.05, 0.62, 0.02), leather('#2a201a'), b);
    strap.position.set(0.02, 1.18, 0.2);
    strap.rotation.z = 0.7;
    const badge = G(b, 0.1, 1.3, 0.215);
    const red = emis('#7a1a10', RED, 0.6);
    M(new THREE.BoxGeometry(0.012, 0.07, 0.008), red, badge);
    M(new THREE.BoxGeometry(0.04, 0.02, 0.008), red, badge).position.y = 0.035;
    M(new THREE.TorusGeometry(0.012, 0.004, 3, 8), red, badge).position.set(0.03, -0.01, 0);
  }
  const iron = metal('#3c4148', { roughness: 0.45 });
  const haft = leather('#3a2a1e');
  if (f.tool === 'pick') {
    // The pick on the right shoulder, its head behind.
    const t = G(b, 0.16, 1.42, -0.02);
    t.rotation.set(0.15, 0, 0.9);
    limb(t, V(0, -0.45, 0.04), V(0, 0.42, -0.04), 0.017, 0.019, haft, 5);
    const head = M(new THREE.BoxGeometry(0.42, 0.045, 0.045), iron, t);
    head.position.set(0, 0.42, -0.04);
    M(new THREE.ConeGeometry(0.024, 0.1, 5), iron, t).position.set(0.24, 0.42, -0.04);
    fig.rig.armR.rotation.set(-0.9, 0, -0.5);
  } else if (f.tool === 'maul') {
    const t = G(fig.rhAbs, 0.26, 0.82, 0.12);
    limb(t, V(0, -0.1, 0), V(0, 0.62, 0), 0.02, 0.022, haft, 5);
    M(new THREE.BoxGeometry(0.11, 0.11, 0.2), iron, t).position.set(0, -0.12, 0);
  } else if (f.tool === 'hammer') {
    // A hammer at the belt, its head down by the hip.
    const t = G(b, -0.2, 0.86, 0.1);
    t.rotation.z = 0.15;
    limb(t, V(0, -0.2, 0), V(0, 0.08, 0), 0.014, 0.016, haft, 5);
    M(new THREE.BoxGeometry(0.1, 0.06, 0.06), iron, t).position.set(0, -0.2, 0);
  }
  return done(c, f.dwarf ? 1.46 : 1.72, () => undefined, fig.rig);
};

const adela = (o?: BaseOpts): Model => folk(o, 921, { coat: '#4a4f57', under: '#3c4047', legs: '#2c2e33', skin: '#c49a84', hair: '#c9c7c2', apron: '#6a625a', tool: 'pick', bun: true, dwarf: false });
const quarryman = (o?: BaseOpts): Model => folk(o, 922, { coat: '#545a62', under: '#46494f', legs: '#2e3036', skin: '#b98a70', hair: '#2e2620', apron: '#5f574f', cap: '#3a3029', tool: 'maul' });
const quarrywoman = (o?: BaseOpts): Model => folk(o, 923, { coat: '#3f454e', under: '#4f535b', legs: '#2b2d32', skin: '#cfa38a', hair: '#3a2a22', apron: '#625a52', tool: 'pick', bun: true });
const brask = (o?: BaseOpts): Model => folk(o, 924, { coat: '#2f3238', under: '#3a3d44', legs: '#24262b', skin: '#b98d76', hair: '#3a3a3e', beard: '#4a4a4e', cloak: '#26282d', tool: 'hammer', dwarf: true, mark: true });
const clanDwarf = (o?: BaseOpts): Model => folk(o, 925, { coat: '#3a3f47', under: '#4a3a32', legs: '#25272c', skin: '#b38670', hair: '#2a2420', beard: '#3a302a', hood: '#2e333a', tool: 'hammer', dwarf: true, mark: true });
const clanDwarf2 = (o?: BaseOpts): Model => folk(o, 926, { coat: '#4a3c34', under: '#3a3f47', legs: '#25272c', skin: '#c49a84', hair: '#4a3a30', hood: '#5a3a30', tool: 'pick', dwarf: true, mark: true });

/** The heart's models by name (null: none of them). */
export const corazonModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'adela':
      return adela(o);
    case 'quarryman':
      return quarryman(o);
    case 'quarrywoman':
      return quarrywoman(o);
    case 'brask':
      return brask(o);
    case 'clanDwarf':
      return clanDwarf(o);
    case 'clanDwarf2':
      return clanDwarf2(o);
    default:
      return null;
  }
};
